import { localized, msg } from '@lit/localize';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { environment } from '../config/environment';
import {
  type AuthProvider,
  authEndpoint,
  authErrorMessage,
  authStateMessage,
  cleanAuthUrl,
  defaultSignInUrl,
  providerSignInUrl,
} from './auth-page-utils';

type AuthMode = 'login' | 'signup';

@customElement('kv-auth-page')
@localized()
export class KvAuthPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: #f6f7f9;
      color: var(--color-app-text, #172033);
      font-family: var(
        --font,
        system-ui,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        sans-serif
      );
      --auth-accent: var(--color-accent, #3f63f3);
      --auth-accent-bg: var(--color-accent-bg, #eef4ff);
      --auth-accent-border: var(
        --color-accent-border,
        rgba(63, 99, 243, 0.28)
      );
      --auth-border: var(--color-app-border, rgba(15, 23, 42, 0.11));
      --auth-muted: var(--color-app-text-secondary, #687386);
      --auth-panel: var(--color-surface, #ffffff);
      --auth-panel-muted: var(--color-surface-muted, #f8fafc);
      --auth-shadow: var(
        --elevation-panel,
        0 10px 24px rgba(15, 23, 42, 0.06)
      );
    }

    :host([color='dark']) {
      background: var(--color-app-bg, #1f1f1f);
      color: var(--color-app-text, #f4f7fb);
    }

    .shell {
      min-height: 100vh;
      display: grid;
      place-items: center;
      padding: 40px 24px;
      box-sizing: border-box;
      position: relative;
      overflow: hidden;
    }

    .shell::before {
      position: absolute;
      inset: 0;
      content: '';
      background-image: url('/img/automation-cockpit-light.webp');
      background-position: center;
      background-size: cover;
      transform: scale(1.02);
    }

    :host([color='dark']) .shell::before {
      background-image: url('/img/automation-cockpit-dark.webp');
    }

    .auth-card {
      width: min(100%, 920px);
      display: grid;
      grid-template-columns: minmax(260px, 0.88fr) minmax(360px, 1fr);
      border: 1px solid var(--auth-border);
      border-radius: var(--radius-panel, 8px);
      background: color-mix(in srgb, var(--auth-panel) 94%, transparent);
      box-shadow: var(--auth-shadow);
      box-sizing: border-box;
      overflow: hidden;
      position: relative;
      z-index: 1;
      backdrop-filter: blur(18px);
    }

    .context-panel {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 32px;
      min-height: 520px;
      padding: 32px;
      background:
        linear-gradient(
          180deg,
          color-mix(in srgb, var(--auth-accent-bg) 78%, transparent),
          color-mix(in srgb, var(--auth-panel-muted) 94%, transparent)
        ),
        var(--auth-panel-muted);
      border-right: 1px solid var(--auth-border);
    }

    .brand-lockup {
      align-items: center;
      display: inline-flex;
      gap: 10px;
    }

    .brand-mark {
      display: block;
      height: 34px;
      object-fit: contain;
      width: 34px;
    }

    .brand-name {
      color: var(--color-app-text, #172033);
      font-size: 16px;
      font-weight: 780;
      line-height: 1;
      margin: 0;
    }

    .context-copy {
      max-width: 310px;
    }

    .context-title {
      color: var(--color-app-text, #172033);
      font-size: 24px;
      font-weight: 750;
      line-height: 1.15;
      margin: 28px 0 0;
    }

    .context-description {
      color: var(--auth-muted);
      font-size: 13px;
      line-height: 1.45;
      margin: 10px 0 0;
    }

    .context-list {
      display: grid;
      gap: 10px;
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .context-item {
      align-items: center;
      color: #344054;
      display: flex;
      font-size: 13px;
      font-weight: 700;
      gap: 10px;
      min-width: 0;
    }

    .context-dot {
      background: var(--auth-accent);
      border-radius: 999px;
      box-shadow: 0 0 0 4px rgba(63, 99, 243, 0.1);
      flex: 0 0 auto;
      height: 8px;
      width: 8px;
    }

    .panel {
      background: color-mix(in srgb, var(--auth-panel) 96%, transparent);
      padding: 32px;
    }

    .brand {
      font-size: 24px;
      font-weight: 750;
      line-height: 1.15;
      margin: 0;
      letter-spacing: 0;
    }

    .form-intro {
      margin-bottom: 22px;
    }

    .form-description {
      margin: 8px 0 0;
      color: var(--auth-muted);
      font-size: 13px;
      line-height: 1.45;
    }

    .tabs {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 4px;
      background: var(--auth-panel-muted);
      border: 1px solid rgba(15, 23, 42, 0.06);
      padding: 4px;
      border-radius: 8px;
      margin-bottom: 18px;
    }

    .tab {
      appearance: none;
      border: 0;
      border-radius: 6px;
      padding: 10px;
      background-color: transparent;
      color: var(--auth-muted);
      font: inherit;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      transition:
        background-color 120ms ease,
        color 120ms ease,
        box-shadow 120ms ease;
    }

    .tab[aria-selected='true'] {
      background-color: var(--auth-panel);
      color: var(--color-app-text, #172033);
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
    }

    .tab:focus-visible,
    .primary:focus-visible,
    .provider:focus-visible,
    .dev-link:focus-visible {
      outline: 2px solid var(--auth-accent);
      outline-offset: 2px;
    }

    form {
      display: grid;
      gap: 13px;
    }

    label {
      display: grid;
      gap: 6px;
      color: #344054;
      font-size: 12px;
      font-weight: 700;
    }

    input {
      width: 100%;
      min-height: 44px;
      border: 1px solid rgba(15, 23, 42, 0.13);
      border-radius: 8px;
      padding: 9px 12px;
      color: var(--color-app-text, #172033);
      background: #fff;
      box-sizing: border-box;
      font: inherit;
      font-size: 14px;
      user-select: text;
      cursor: text;
      transition:
        background-color 120ms ease,
        border-color 120ms ease,
        box-shadow 120ms ease;
    }

    input:focus {
      outline: none;
      border-color: var(
        --color-accent-border-strong,
        rgba(63, 99, 243, 0.62)
      );
      box-shadow: 0 0 0 3px var(--color-accent-border-subtle);
    }

    input::placeholder {
      color: #99a2b1;
    }

    .primary,
    .provider {
      min-height: 44px;
      border-radius: 8px;
      font: inherit;
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      transition:
        background-color 120ms ease,
        border-color 120ms ease,
        box-shadow 120ms ease,
        transform 120ms ease;
    }

    .primary {
      appearance: none;
      border: 1px solid var(--auth-accent);
      background: var(--auth-accent);
      color: #fff;
      margin-top: 6px;
      box-shadow: 0 10px 18px rgba(63, 99, 243, 0.18);
    }

    .primary:hover:not(:disabled),
    .provider:hover {
      transform: translateY(-1px);
    }

    .primary:disabled {
      opacity: 0.6;
      cursor: wait;
    }

    .divider {
      display: flex;
      align-items: center;
      gap: 10px;
      color: var(--auth-muted);
      font-size: 12px;
      font-weight: 650;
      margin: 20px 0;
    }

    .divider::before,
    .divider::after {
      content: '';
      height: 1px;
      flex: 1;
      background: var(--auth-border);
    }

    .providers {
      display: grid;
      gap: 8px;
    }

    .provider {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      border: 1px solid rgba(15, 23, 42, 0.12);
      background: var(--auth-panel-muted);
      color: var(--color-app-text, #172033);
      text-decoration: none;
    }

    .provider-icon {
      flex: 0 0 auto;
      height: 18px;
      width: 18px;
    }

    .provider:hover {
      background: var(--auth-accent-bg);
      border-color: var(--auth-accent-border);
      color: var(--auth-accent);
      box-shadow: 0 8px 16px rgba(15, 23, 42, 0.06);
    }

    .message,
    .error {
      border-radius: 8px;
      padding: 11px 12px;
      font-size: 13px;
      line-height: 1.4;
      margin-bottom: 14px;
    }

    .message {
      background: #ecfdf3;
      border: 1px solid #abefc6;
      color: #027a48;
    }

    .error {
      background: #fef3f2;
      border: 1px solid #fecdca;
      color: #b42318;
    }

    .dev-link {
      display: inline-block;
      color: var(--auth-accent);
      cursor: pointer;
      font-weight: 700;
      margin-top: 8px;
    }

    :host([color='dark']) .auth-card {
      background: color-mix(in srgb, var(--auth-panel) 92%, transparent);
    }

    :host([color='dark']) .context-panel {
      background:
        linear-gradient(
          180deg,
          rgba(63, 99, 243, 0.16),
          color-mix(in srgb, var(--auth-panel) 96%, transparent)
        ),
        var(--auth-panel);
    }

    :host([color='dark']) .brand-name,
    :host([color='dark']) .context-title,
    :host([color='dark']) .context-item,
    :host([color='dark']) .brand,
    :host([color='dark']) .tab[aria-selected='true'],
    :host([color='dark']) label,
    :host([color='dark']) input,
    :host([color='dark']) .provider {
      color: #f4f7fb;
    }

    :host([color='dark']) .tabs {
      background: #2f3035;
      border-color: var(--auth-border);
    }

    :host([color='dark']) .tab[aria-selected='true'] {
      background-color: var(--auth-accent-bg);
    }

    :host([color='dark']) input {
      background: #1f2023;
      border-color: rgba(255, 255, 255, 0.16);
    }

    :host([color='dark']) .provider {
      background: #2f3035;
      border-color: rgba(255, 255, 255, 0.14);
    }

    :host([color='dark']) .provider:hover {
      background: rgba(63, 99, 243, 0.22);
      border-color: rgba(138, 164, 255, 0.34);
      color: #dbe5ff;
    }

    @media (min-width: 900px) {
      .auth-card {
        width: min(100%, 920px);
      }
    }

    @media (max-width: 820px) {
      .auth-card {
        grid-template-columns: 1fr;
      }

      .context-panel {
        min-height: auto;
        border-right: 0;
        border-bottom: 1px solid var(--auth-border);
      }

      .context-copy {
        max-width: none;
      }

      .context-title {
        margin-top: 22px;
      }
    }

    @media (max-width: 560px) {
      .shell {
        align-items: end;
        padding: 16px;
      }

      .auth-card {
        width: 100%;
      }

      .context-panel,
      .panel {
        padding: 24px;
      }
    }
  `;

  @state() private accessor mode: AuthMode = 'login';
  @state() private accessor email = '';
  @state() private accessor password = '';
  @state() private accessor name = '';
  @state() private accessor loading = false;
  @state() private accessor message: string | null = null;
  @state() private accessor errorMessage: string | null = null;
  @state() private accessor verificationUrl: string | null = null;

  @property({ type: String, reflect: true })
  accessor color: 'light' | 'dark' = 'light';

  connectedCallback(): void {
    super.connectedCallback();
    const state = authStateMessage(window.location.href);
    this.message = state.message;
    this.errorMessage = state.errorMessage;
  }

  private async submit(event: Event) {
    event.preventDefault();
    this.loading = true;
    this.message = null;
    this.errorMessage = null;
    this.verificationUrl = null;

    try {
      if (this.mode === 'signup') {
        await this.signup();
      } else {
        await this.login();
      }
    } finally {
      this.loading = false;
    }
  }

  private async signup() {
    const res = await fetch(this.authEndpoint('signup'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: this.email,
        password: this.password,
        name: this.name || undefined,
      }),
    });
    const payload = await readJson(res);
    if (!res.ok) {
      this.errorMessage = authErrorMessage(payload, msg('Signup failed.'));
      return;
    }

    this.message = payload.verificationEmailSent
      ? msg('Check your email to verify your account.')
      : msg('Account created. Use the verification link below.');
    this.verificationUrl =
      typeof payload.verificationUrl === 'string'
        ? payload.verificationUrl
        : null;
    this.mode = 'login';
  }

  private async login() {
    const res = await fetch(this.authEndpoint('password-login'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: this.email,
        password: this.password,
      }),
    });
    const payload = await readJson(res);
    if (!res.ok) {
      this.errorMessage = authErrorMessage(payload, msg('Sign in failed.'));
      return;
    }

    window.location.replace(this.cleanCurrentUrl());
  }

  private async startProviderSignIn(event: Event, provider: AuthProvider) {
    event.preventDefault();
    this.clearPasswordForNavigation();
    this.loading = true;
    this.message = null;
    this.errorMessage = null;
    this.verificationUrl = null;

    try {
      const res = await fetch(this.authEndpoint('csrf'), {
        credentials: 'include',
      });
      const payload = await readJson(res);
      const csrfToken =
        typeof payload.csrfToken === 'string' ? payload.csrfToken : null;
      if (!res.ok || !csrfToken) {
        this.errorMessage = msg('OAuth sign-in could not be started.');
        this.loading = false;
        return;
      }

      postForm(this.providerUrl(provider), {
        csrfToken,
        callbackUrl: this.cleanCurrentUrl(),
      });
    } catch {
      this.errorMessage = msg('OAuth sign-in could not be started.');
      this.loading = false;
    }
  }

  private clearPasswordForNavigation() {
    this.password = '';
    const passwordInput = this.renderRoot.querySelector<HTMLInputElement>(
      'input[type="password"]'
    );
    if (passwordInput) {
      passwordInput.value = '';
    }
  }

  private setMode(mode: AuthMode) {
    this.mode = mode;
    this.errorMessage = null;
    this.message = null;
    this.verificationUrl = null;
  }

  private authEndpoint(path: string): string {
    return authEndpoint(environment.apiBaseUrl, window.location.origin, path);
  }

  private providerUrl(provider: AuthProvider): string {
    return providerSignInUrl(
      provider,
      environment.apiBaseUrl,
      window.location.href
    );
  }

  private defaultSignInUrl(): string {
    return defaultSignInUrl(environment.apiBaseUrl, window.location.href);
  }

  private cleanCurrentUrl(): string {
    return cleanAuthUrl(window.location.href);
  }

  private get heading(): string {
    return this.mode === 'signup'
      ? msg('Create your Klarvos account')
      : msg('Sign in to Klarvos');
  }

  private get description(): string {
    return this.mode === 'signup'
      ? msg('Start with a workspace for workflows, chat, and data connections.')
      : msg('Continue to your workflows, chat and connected tools.');
  }

  render() {
    return html`
      <main class="shell">
        <section class="auth-card" aria-label=${msg('Klarvos authentication')}>
          <aside class="context-panel" aria-label=${msg('Klarvos overview')}>
            <div class="context-copy">
              <div class="brand-lockup">
                <img
                  class="brand-mark"
                  src="/logo.svg"
                  alt=""
                  aria-hidden="true"
                />
                <p class="brand-name">Klarvos</p>
              </div>
              <h2 class="context-title">
                ${msg('Klarvos brings firm work together')}
              </h2>
              <p class="context-description">
                ${msg(
                  'Work with DATEV data, documents, and firm knowledge in chat and workflows.'
                )}
              </p>
            </div>
            <ul class="context-list" aria-label=${msg('Klarvos capabilities')}>
              <li class="context-item">
                <span class="context-dot" aria-hidden="true"></span>
                ${msg('Answers with DATEV context')}
              </li>
              <li class="context-item">
                <span class="context-dot" aria-hidden="true"></span>
                ${msg('Automate recurring work')}
              </li>
              <li class="context-item">
                <span class="context-dot" aria-hidden="true"></span>
                ${msg('Prepare client communication')}
              </li>
            </ul>
          </aside>

          <div class="panel">
            <div class="form-intro">
              <h1 class="brand">${this.heading}</h1>
              <p class="form-description">${this.description}</p>
            </div>

            <div class="tabs" role="tablist" aria-label=${msg('Authentication mode')}>
              <button
                class="tab"
                type="button"
                role="tab"
                aria-selected=${this.mode === 'login' ? 'true' : 'false'}
                @click=${() => this.setMode('login')}
              >
                ${msg('Login')}
              </button>
              <button
                class="tab"
                type="button"
                role="tab"
                aria-selected=${this.mode === 'signup' ? 'true' : 'false'}
                @click=${() => this.setMode('signup')}
              >
                ${msg('Signup')}
              </button>
            </div>

            ${
              this.message
                ? html`<div class="message">
                    ${this.message}${this.renderDevLink()}
                  </div>`
                : nothing
            }
            ${
              this.errorMessage
                ? html`<div class="error">${this.errorMessage}</div>`
                : nothing
            }

            <form @submit=${this.submit}>
              ${
                this.mode === 'signup'
                  ? html`
                      <label>
                        ${msg('Name')}
                        <input
                          autocomplete="name"
                          placeholder=${msg('Your name')}
                          .value=${this.name}
                          @input=${(event: InputEvent) =>
                            (this.name = inputValue(event))}
                        />
                      </label>
                    `
                  : nothing
              }

              <label>
                ${msg('Email')}
                <input
                  type="email"
                  autocomplete="email"
                  placeholder="you@example.com"
                  required
                  .value=${this.email}
                  @input=${(event: InputEvent) =>
                    (this.email = inputValue(event))}
                />
              </label>

              <label>
                ${msg('Password')}
                <input
                  type="password"
                  autocomplete=${
                    this.mode === 'signup' ? 'new-password' : 'current-password'
                  }
                  minlength="8"
                  required
                  placeholder=${msg('At least 8 characters')}
                  .value=${this.password}
                  @input=${(event: InputEvent) =>
                    (this.password = inputValue(event))}
                />
              </label>

              <button class="primary" type="submit" ?disabled=${this.loading}>
                ${
                  this.loading
                    ? msg('Working...')
                    : this.mode === 'signup'
                      ? msg('Create account')
                      : msg('Sign in')
                }
              </button>
            </form>

            <div class="divider">${msg('or continue with')}</div>
            <div class="providers">
              <a
                class="provider"
                href=${this.defaultSignInUrl()}
                @click=${(event: Event) =>
                  this.startProviderSignIn(event, 'google')}
              >
                <svg
                  class="provider-icon"
                  viewBox="0 0 18 18"
                  aria-hidden="true"
                >
                  <path
                    fill="#4285f4"
                    d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.08-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
                  />
                  <path
                    fill="#34a853"
                    d="M9 18c2.43 0 4.47-.81 5.95-2.18l-2.91-2.26c-.8.54-1.83.86-3.04.86-2.35 0-4.33-1.58-5.04-3.71H.96v2.33C2.44 15.98 5.48 18 9 18Z"
                  />
                  <path
                    fill="#fbbc05"
                    d="M3.96 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3-2.33Z"
                  />
                  <path
                    fill="#ea4335"
                    d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l3 2.33C4.67 5.16 6.65 3.58 9 3.58Z"
                  />
                </svg>
                Google
              </a>
              <a
                class="provider"
                href=${this.defaultSignInUrl()}
                @click=${(event: Event) =>
                  this.startProviderSignIn(event, 'azure-ad')}
              >
                <svg
                  class="provider-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill="#0078d4"
                    d="M13.5 2 5.6 18.6H2.1L8.5 7.7 13.5 2Z"
                  />
                  <path
                    fill="#50a7f9"
                    d="M14.3 3.4 22 22H7.3l2.1-3.4h7.4l-4.2-9.2 1.7-6Z"
                  />
                </svg>
                Azure AD
              </a>
            </div>
          </div>
        </section>
      </main>
    `;
  }

  private renderDevLink() {
    return this.verificationUrl
      ? html`<br /><a class="dev-link" href=${this.verificationUrl}>${msg('Open verification link')}</a>`
      : nothing;
  }
}

async function readJson(res: Response): Promise<Record<string, unknown>> {
  try {
    const payload = await res.json();
    return payload && typeof payload === 'object'
      ? (payload as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function inputValue(event: InputEvent): string {
  return (event.currentTarget as HTMLInputElement).value;
}

function postForm(action: string, fields: Record<string, string>): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  form.style.display = 'none';

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.append(input);
  }

  document.body.append(form);
  form.submit();
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-auth-page': KvAuthPage;
  }
}
